// The KNOWLEDGE slice of the browser transport — the Memory / Files / Team / Settings / Task-detail /
// Email legs, with the read endpoints and decision routes they are built from. Split out of
// `api-client.ts` to hold that file's size ceiling (the same move `chat-stream.ts` and
// `directory-api.ts` made); same posture as every other leg: same-origin only, no Actor/approval field
// on the wire (the server resolves the Actor — mass-assignment is banned), an uncertain Task-message retry
// reuses its exact correlation id but no mutation is retried under a fresh identity,
// and a refusal comes back as a sanitised user-facing note (G-197).

import { streamRun } from './chat-stream.ts';
import { httpDirectoryRoutes } from './directory-api.ts';
import { postIntent, safeNote, API_PREFIX } from './post-intent.ts';
import type { ChatSendResult } from './projections.ts';
import type {
  DirectoryProjection,
  FilesProjection,
  MemoryBrowserProjection,
  MemoryReviewProjection,
  MemorySearchProjection,
  MessageReaderProjection,
  SettingsProjection,
  TaskDetailProjection,
} from './know-projections.ts';
import type { ChatStreamHandlers, SurfaceApi } from './api-client.ts';

/** The Knowledge-batch read endpoints (mirror the Stage-1 DASHBOARD_API_ROUTES knowledge rows). */
const MEMORY_REVIEW_PATH = `${API_PREFIX}/memory/review`;
const FILES_PATH = `${API_PREFIX}/files`;
const TEAM_PATH = `${API_PREFIX}/team`;
const SETTINGS_PATH = `${API_PREFIX}/settings`;
const taskDetailPath = (taskId: string): string =>
  `${API_PREFIX}/tasks/${encodeURIComponent(taskId)}/detail`;
const taskReviewPath = (taskId: string): string =>
  `${API_PREFIX}/tasks/${encodeURIComponent(taskId)}/review`;
const taskOwnerPath = (taskId: string): string =>
  `${API_PREFIX}/tasks/${encodeURIComponent(taskId)}/owner`;
const taskMessagesPath = (taskId: string): string =>
  `${API_PREFIX}/tasks/${encodeURIComponent(taskId)}/messages`;
const taskRunStreamPath = (taskId: string, runId: string): string =>
  `${API_PREFIX}/tasks/${encodeURIComponent(taskId)}/runs/${encodeURIComponent(runId)}/stream`;
const messagePath = (messageId: string): string =>
  `${API_PREFIX}/messages/${encodeURIComponent(messageId)}`;
const memoryBrowserPath = (areaPath: string, focusNodeId?: string): string => {
  const params = new URLSearchParams({ area: areaPath });
  if (focusNodeId !== undefined) params.set('node', focusNodeId);
  return `${API_PREFIX}/memory?${params.toString()}`;
};
const memorySearchPath = (query: string): string => {
  const params = new URLSearchParams({ q: query });
  return `${API_PREFIX}/memory/search?${params.toString()}`;
};

/** The Knowledge-batch decision routes: the Memory decision → C, and the directory actions → A. */
const MEMORY_PROPOSAL_DECIDE_PATH = `${API_PREFIX}/memory/proposal-decide`;
const MEMORY_CONFLICT_RESOLVE_PATH = `${API_PREFIX}/memory/conflict-resolve`;

/** GET a typed projection from a read endpoint. A non-OK response is surfaced honestly (never fabricated). */
async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    // Fail honestly — the surface renders its error state (P-SELF); no fabricated projection.
    throw new Error(`read failed (${String(response.status)})`);
  }
  return (await response.json()) as T;
}

/** GET a projection that may be legitimately absent (a 404 → undefined, honest not-found, never fabricated). */
async function getMaybe<T>(path: string): Promise<T | undefined> {
  const response = await fetch(path, {
    method: 'GET',
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (response.status === 404) return undefined;
  if (!response.ok) throw new Error(`read failed (${String(response.status)})`);
  return (await response.json()) as T;
}

/** Task is path-scoped; requestId is correlation only. The server resolves Actor and canonical Session. */
async function postTaskMessage(
  taskId: string,
  message: string,
  requestId: string,
): Promise<ChatSendResult> {
  const response = await fetch(taskMessagesPath(taskId), {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ message, requestId }),
  });
  if (!response.ok) {
    // A refused send carries the API's sanitised note, never an internal error (G-197).
    return { kind: 'refused', note: await safeNote(response) };
  }
  return (await response.json()) as ChatSendResult;
}

function streamTaskRun(taskId: string, runId: string, handlers: ChatStreamHandlers): () => void {
  return streamRun(taskRunStreamPath(taskId, runId), handlers);
}

/** The Task-detail decision legs: the owner's review response and the creator's Draft owner change. */
function httpTaskDecisionRoutes(): Pick<SurfaceApi, 'reviewTask' | 'reassignTaskOwner'> {
  return {
    reviewTask: (taskId, action, input) =>
      postIntent(
        taskReviewPath(taskId),
        action === 'defer'
          ? {
              action,
              reason: input.reason,
              revisitAt: input.revisitAt ?? '',
            }
          : { action, expectedRevision: input.expectedRevision, reason: input.reason },
      ),
    // The body carries the Team-directory NAME, never a User id: the server resolves who that is.
    reassignTaskOwner: (taskId, expectedRevision, ownerName) =>
      postIntent(taskOwnerPath(taskId), { expectedRevision, ownerName }),
  };
}

/** The KNOWLEDGE slice of the real transport (Memory / Files / Team / Settings / Task-detail / Email). */
export function httpKnowledgeApi(): Pick<
  SurfaceApi,
  | 'getMemoryBrowser'
  | 'getMemorySearch'
  | 'getMemoryReview'
  | 'getFiles'
  | 'getDirectory'
  | 'getSettings'
  | 'getTaskDetail'
  | 'reviewTask'
  | 'reassignTaskOwner'
  | 'sendTaskMessage'
  | 'streamTaskRun'
  | 'getMessage'
  | 'routeMemoryDecision'
  | 'routeDirectoryPropose'
  | 'routeDirectoryResolve'
  | 'routeContactAction'
> {
  return {
    getMemoryBrowser: (areaPath, focusNodeId) =>
      getJson<MemoryBrowserProjection>(memoryBrowserPath(areaPath, focusNodeId)),
    getMemorySearch: (query) => getJson<MemorySearchProjection>(memorySearchPath(query)),
    getMemoryReview: () => getJson<MemoryReviewProjection>(MEMORY_REVIEW_PATH),
    getFiles: () => getJson<FilesProjection>(FILES_PATH),
    getDirectory: () => getJson<DirectoryProjection>(TEAM_PATH),
    getSettings: () => getJson<SettingsProjection>(SETTINGS_PATH),
    getTaskDetail: (taskId) => getJson<TaskDetailProjection>(taskDetailPath(taskId)),
    ...httpTaskDecisionRoutes(),
    sendTaskMessage: (taskId, message, requestId) => postTaskMessage(taskId, message, requestId),
    streamTaskRun,
    getMessage: (messageId) => getMaybe<MessageReaderProjection>(messagePath(messageId)),
    // The body carries the SERVER intent field names (deep-sweep 2026-07-16 N4: `{objectId}` fell through
    // the server's typed intent switch and no-opped 200-null — the wire contract is proposalId/conflictId).
    routeMemoryDecision: (kind, objectId, decision) =>
      postIntent(
        kind === 'proposal_decide' ? MEMORY_PROPOSAL_DECIDE_PATH : MEMORY_CONFLICT_RESOLVE_PATH,
        kind === 'proposal_decide' ? { proposalId: objectId, decision } : { conflictId: objectId },
      ),
    // Each directory leg sends the SERVER intent's exact field names (the N4 wire-contract lesson):
    // propose carries {changeKind, summary, targetUserId?}; resolve carries {proposalId, decision}.
    ...httpDirectoryRoutes(),
  };
}
